import { useMemo } from 'react';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { useSaSorgu } from '../useSatisVerisi';
import { fisOzeti, magazaSepeti, sayi, type KanalSatiri, type Sayisal } from '../ozetHesaplari';
import { kanalSuz, subeSuz } from '../subeSuzgeci';
import { sepetHesaplanirMi } from '../kanal';
import type { DetayGovdeProps } from './detayTipleri';
import { DagilimTablosu, DeltaHucre, DetayBolum, DetayMesaj, DetayYukleniyor, OzetKutulari } from './ortak';
import { kanalAnahtari, kanalKirilimi, payHesapla } from './KanalDetay';

/** 990017 Şube × Kategori Satış (125-…-repoint.sql:157-167; mutabik, metni DEGISMEZ). */
export interface SubeKategoriSatiri {
  magaza: string;
  kategori: string;
  ciro: Sayisal;
  miktar: Sayisal;
}

export function subeKategorileri(rows: SubeKategoriSatiri[], subeAdi: string) {
  const s = rows.filter((r) => String(r.magaza) === subeAdi);
  const toplam = s.reduce((t, r) => t + sayi(r.ciro), 0);
  return s
    .map((r) => ({ kategori: String(r.kategori), ciro: sayi(r.ciro), miktar: sayi(r.miktar), pay: payHesapla(sayi(r.ciro), toplam) }))
    .sort((a, b) => b.ciro - a.ciro);
}

/**
 * M3 — sube detayi (spec §7.5). Kanal kirilimi 990040'tan (sayfa onbellegi; sayfanin kanal suzgeci uygulanir,
 * SOZLESME SAPMASI 10). Kategori kirilimi 990017'den: TEK istek, yalniz tarihlerle (017 @LOKASYON/@KANAL
 * bildirmez) -> ayni donemde her sube ayni anahtari paylasir; sube istemcide suzulur, kanal suzulemez (not).
 */
export function SubeDetay({ kaynaklar, donem, oncekiDonem, kanal, subeAdi }: DetayGovdeProps & { subeAdi: string }) {
  const buQ = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem, sube: null, kanal: null }, true, { onbellekten: true });
  const oncekiQ = useSaSorgu<KanalSatiri>(
    kaynaklar.kanal040, { donem: oncekiDonem, sube: null, kanal: null }, true, { onbellekten: true },
  );
  const katQ = useSaSorgu<SubeKategoriSatiri>(kaynaklar.subeKategori017, { donem, sube: null, kanal: null });

  const bu = buQ.data?.data;
  const onceki = oncekiQ.data?.data;
  const katVeri = katQ.data?.data;
  const d = useMemo(() => {
    const buS = kanalSuz(subeSuz(bu ?? [], subeAdi), kanal);
    const oncekiS = kanalSuz(subeSuz(onceki ?? [], subeAdi), kanal);
    const oz = fisOzeti(buS, null, null);
    const oncekiKanal = new Map(kanalKirilimi(oncekiS).map((k) => [kanalAnahtari(k.grup, k.kanal), k.ciro]));
    return {
      satirVar: buS.length > 0,
      ciro: oz.ciro,
      fis: oz.fis,
      oncekiCiro: oncekiS.length > 0 ? fisOzeti(oncekiS, null, null).ciro : null,
      sepet: magazaSepeti(bu ?? [], subeAdi).sepet,
      kanallar: kanalKirilimi(buS).map((k) => ({
        ...k,
        pay: payHesapla(k.ciro, oz.ciro),
        oncekiCiro: oncekiKanal.get(kanalAnahtari(k.grup, k.kanal)) ?? null,
      })),
    };
  }, [bu, onceki, subeAdi, kanal]);
  const kategoriler = useMemo(() => subeKategorileri(katVeri ?? [], subeAdi), [katVeri, subeAdi]);

  let kanalBolumu;
  if (!kaynaklar.kanal040) kanalBolumu = <DetayMesaj testid="sube-detay-kaynak-yok">Satış kanalı raporu bu firmada tanımlı değil.</DetayMesaj>;
  else if (buQ.isLoading || oncekiQ.isLoading) kanalBolumu = <DetayYukleniyor testid="sube-detay-yukleniyor" />;
  else if (buQ.isError) kanalBolumu = <DetayMesaj testid="sube-detay-hata">Şube verisi yüklenemedi.</DetayMesaj>;
  else if (!d.satirVar) kanalBolumu = <DetayMesaj testid="sube-detay-bos">Bu şubede dönemde satış yok.</DetayMesaj>;
  else
    kanalBolumu = (
      <>
        <OzetKutulari
          kutular={[
            {
              testid: 'sube-detay-ozet-ciro',
              etiket: 'Ciro',
              deger: fmtTRY(d.ciro),
              ek: <>Önceki döneme göre <DeltaHucre bu={d.ciro} onceki={d.oncekiCiro} /></>,
            },
            { testid: 'sube-detay-ozet-fis', etiket: 'Fiş', deger: fmtNum(d.fis) },
            ...(sepetHesaplanirMi(kanal)
              ? [{ testid: 'sube-detay-ozet-sepet', etiket: 'Mağaza sepeti', deger: d.sepet === null ? '—' : fmtTRY(d.sepet) }]
              : []),
          ]}
        />
        <DetayBolum baslik="Kanal kırılımı">
          <DagilimTablosu
            testid="sube-detay-kanallar"
            satirTestid="sube-detay-kanal"
            baslikSutunu="Kanal"
            deltaGoster
            satirlar={d.kanallar.map((k) => ({
              ad: k.kanal,
              ek: k.grup !== k.kanal ? k.grup : undefined,
              ciro: k.ciro,
              pay: k.pay,
              sayac: k.fis,
              oncekiCiro: k.oncekiCiro,
            }))}
          />
        </DetayBolum>
      </>
    );

  let kategoriIcerik;
  if (!kaynaklar.subeKategori017)
    kategoriIcerik = <DetayMesaj testid="sube-detay-kategori-yok">Kategori kırılımı bu firmada tanımlı değil.</DetayMesaj>;
  else if (katQ.isLoading) kategoriIcerik = <DetayYukleniyor testid="sube-detay-kategori-yukleniyor" />;
  else if (katQ.isError) kategoriIcerik = <DetayMesaj testid="sube-detay-kategori-hata">Kategori kırılımı yüklenemedi.</DetayMesaj>;
  else if (kategoriler.length === 0)
    kategoriIcerik = <DetayMesaj testid="sube-detay-kategori-bos">Bu şubede dönemde kategori satışı yok.</DetayMesaj>;
  else
    kategoriIcerik = (
      <DagilimTablosu
        testid="sube-detay-kategoriler"
        satirTestid="sube-detay-kategori"
        baslikSutunu="Kategori"
        sayacBasligi="Miktar"
        satirlar={kategoriler.map((k) => ({ ad: k.kategori, ciro: k.ciro, pay: k.pay, sayac: k.miktar }))}
      />
    );

  return (
    <div data-testid="sube-detay" className="flex flex-col gap-4">
      {kanalBolumu}
      <DetayBolum baslik="Kategori kırılımı">
        {kanal !== null && (
          <p data-testid="sube-detay-kanal-notu" className="text-[12px] text-ink-500">
            Kanal süzgeci bu bölüme uygulanmaz — kategori raporu kanala ayrışmıyor, tüm kanallar gösterilir.
          </p>
        )}
        {kategoriIcerik}
      </DetayBolum>
    </div>
  );
}
